import { Message, Resource } from '@nextforce/api'
import { Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import {
  useFileDetails,
  useFolderDetails,
} from 'file_folder_repository/queries/details'
import {
  FileListItem,
  FolderListItem,
} from 'file_folder_repository/views/list_item'
import moment from 'moment'
import { useMemo } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { RoundedButton } from 'shared/components/rounded-button'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { getDarkShadeColor } from 'shared/utils'

export function ShareResourceMessageView({
  message,
  position,
  onLongPress,
  onPressAcknowledgeResource,
}: {
  message: Message
  position: 'left' | 'right'
  onLongPress: () => void
  onPressAcknowledgeResource: (resource: Resource) => void
}) {
  const styles = useStyles()
  const { theme } = useTheme()
  const fileDetailsQuery = useFileDetails(
    message.subTypeId,
    message.subType === 'shareFile',
    false
  )
  const folderDetailsQuery = useFolderDetails(
    message.subTypeId,
    message.subType === 'shareFolder'
  )
  const resourceDetailsQuery = useMemo(() => {
    if (message.subType === 'shareFile') {
      return fileDetailsQuery
    }
    return folderDetailsQuery
  }, [message.subType, fileDetailsQuery, folderDetailsQuery])

  const hasAcknowledgement =
    resourceDetailsQuery.data?.type === 'file' &&
    !!resourceDetailsQuery.data.resourceAcknowledgements?.length

  return (
    <View style={styles.container}>
      {resourceDetailsQuery.isLoading ? (
        <View style={styles.shareResourceMessageContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator color={theme.colors.primary} />
          </View>
        </View>
      ) : (
        <View style={styles.shareResourceMessageContainer}>
          {message.subType === 'shareFile' ? (
            fileDetailsQuery.error ? (
              <ResourceErrorView error={fileDetailsQuery.error as Error} />
            ) : fileDetailsQuery.data ? (
              <FileListItem
                file={fileDetailsQuery.data}
                onPress={() => null}
                onLongPress={onLongPress}
                style={styles.fileFolderListItemStyle}
                titleStyle={
                  position === 'right'
                    ? styles.fileFolderListItemTextSelf
                    : styles.fileFolderListItemTextOther
                }
                descriptionStyle={
                  position === 'right'
                    ? styles.fileFolderListItemTextSelf
                    : styles.fileFolderListItemTextOther
                }
                infoContainerStyle={styles.fileFolderListItemContainer}
                thumbnailHeight={100}
                thumbnailWidth={100}
                thumbnailIconSize={50}
                metaDataType="none"
                titleDescriptionContainerStyle={
                  styles.fileFolderListItemContainer
                }
              />
            ) : folderDetailsQuery.data ? (
              <FolderListItem
                folder={folderDetailsQuery.data}
                onPress={() => null}
                onLongPress={() => null}
                style={styles.fileFolderListItemStyle}
                titleStyle={
                  position === 'right'
                    ? styles.fileFolderListItemTextSelf
                    : styles.fileFolderListItemTextOther
                }
                descriptionStyle={
                  position === 'right'
                    ? styles.fileFolderListItemTextSelf
                    : styles.fileFolderListItemTextOther
                }
                infoContainerStyle={styles.fileFolderListItemContainer}
                thumbnailHeight={100}
                thumbnailWidth={100}
                thumbnailIconSize={50}
                metaDataType="none"
                titleDescriptionContainer={styles.fileFolderListItemContainer}
              />
            ) : null
          ) : null}
          {hasAcknowledgement ? (
            <ResourceAcknowledgementView
              resource={resourceDetailsQuery.data}
              onPressAcknowledgeResource={onPressAcknowledgeResource}
            />
          ) : null}
        </View>
      )}
    </View>
  )
}

const ResourceErrorView = ({ error }: { error: Error | undefined }) => {
  const { theme } = useTheme()
  const styles = useStyles()
  if (!error) {
    return null
  }
  const title =
    error.name === 'ACCESS_DENIED' ? t('resourceAccessDenied') : error.message
  return (
    <View style={styles.loadingContainer}>
      <Icon
        name="alert-circle"
        type="feather"
        size={30}
        color={theme.colors.primary}
      />
      <Text style={styles.errorTitle}>{title}</Text>
    </View>
  )
}

interface AcknowledgeResourceProps {
  resource: Resource | undefined
  onPressAcknowledgeResource: (resource: Resource) => void
}

const ResourceAcknowledgementView = ({
  resource,
  onPressAcknowledgeResource,
}: AcknowledgeResourceProps) => {
  const styles = useStyles()
  if (resource?.type !== 'file' || !resource.resourceAcknowledgements) {
    return null
  }
  const acknowledgement = resource.resourceAcknowledgements[0]
  const requester = acknowledgement.requester
  const requireSign = acknowledgement.requireSign
  const hasAcknowledged = acknowledgement.isAcknowledged
  const dueDate = acknowledgement.dueDate
  return (
    <View style={styles.resourceAcknowledgementContainer}>
      <View style={styles.resourceAcknowledgementDescriptionContainer}>
        <View style={styles.senderRow}>
          <Text style={styles.label}>{t('from')}</Text>
          <UserAvatar user={requester} size={30} />
          <Text
            style={styles.name}
          >{`${requester?.firstName} ${requester?.lastName}`}</Text>
        </View>
        <DueDateRow dueDate={dueDate} />
        <Text style={styles.label}>
          {`${requireSign ? t('requiresSign') : t('requiresAck')} :`}
        </Text>
        <Text style={styles.acknowledgementStatement}>
          {acknowledgement.statement}
        </Text>
      </View>
      <RoundedButton
        title={getButtonTitle(hasAcknowledged, requireSign)}
        onPress={() => onPressAcknowledgeResource(resource)}
        disabled={hasAcknowledged}
        emphasized
      />
    </View>
  )
}

function DueDateRow({ dueDate }: { dueDate: number }) {
  const styles = useStyles()
  const { theme } = useTheme()

  const now = moment()
  const due = moment(dueDate)
  const diffInDays = due.diff(now, 'days')
  const diffInHours = due.diff(now, 'hours')

  const isOverdue = diffInHours < 0

  let dueDateString = ''

  if (diffInDays >= 1) {
    dueDateString = `${t('days', {
      count: diffInDays,
    })}`
  } else if (diffInHours > 0) {
    if (diffInHours === 1) {
      dueDateString = `${diffInHours} ${t('hour')}`
    } else {
      dueDateString = `${diffInHours} ${t('hours')}`
    }
  } else {
    dueDateString = t('overdue')
  }

  return (
    <View style={styles.dueDateRowContainer}>
      <Icon
        name="clock"
        type="feather"
        size={20}
        style={{ marginRight: 5 }}
        color={isOverdue ? theme.colors.primary : undefined}
      />
      <Text
        style={[
          styles.label,
          isOverdue ? { color: theme.colors.primary } : null,
        ]}
      >{`${t('dueIn')} :`}</Text>
      <View
        style={[
          styles.dueDateContainer,
          diffInDays <= 1
            ? !isOverdue
              ? { borderColor: 'pink' }
              : { borderColor: theme.colors.primary }
            : null,
        ]}
      >
        <Text
          style={[
            styles.dueDateValueText,
            isOverdue ? { color: theme.colors.primary } : null,
          ]}
        >
          {dueDateString}
        </Text>
      </View>
    </View>
  )
}

function getButtonTitle(hasAcknowledged: boolean, requireSign: boolean) {
  if (hasAcknowledged) {
    if (requireSign) {
      return t('signed')
    }
    return t('acknowledged')
  }
  if (requireSign) {
    return t('openAndSign')
  }
  return t('openAndAck')
}

const useStyles = makeStyles(theme => ({
  container: { width: 300, paddingTop: 3 },
  fileFolderListItemContainer: {
    maxWidth: 175,
    justifyContent: 'center',
  },
  fileFolderListItemTextOther: { color: theme.colors?.black },
  fileFolderListItemTextSelf: { color: 'black' },
  fileFolderListItemStyle: {
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor(0.1) : getDarkShadeColor(0.05),
  },
  loadingContainer: {
    height: 125,
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: { marginRight: 10, fontSize: 16, fontWeight: 'bold' },
  dueDateContainer: {
    padding: 5,
    paddingHorizontal: 10,
    borderColor: theme.colors?.grey3,
    borderWidth: 1,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
  },
  shareResourceMessageContainer: {
    margin: 5,
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor(0.1) : getDarkShadeColor(0.01),
    borderRadius: 15,
    paddingTop: 3,
  },
  resourceAcknowledgementContainer: {
    paddingHorizontal: 15,
    paddingBottom: 10,
  },
  resourceAcknowledgementDescriptionContainer: { paddingVertical: 5 },
  senderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  name: { marginLeft: 5, fontSize: 14 },
  acknowledgementStatement: { marginVertical: 10 },
  dueDateRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  dueDateValueText: { fontSize: 16, fontWeight: 'bold' },
  errorTitle: { marginTop: 15 },
}))
